/**
 * O VALOR-HORA POR FUNÇÃO (0095), do lado da tela — o que a mesa e o celular
 * dividem, fora do componente, onde o teste alcança.
 *
 * O valor é da FUNÇÃO, nunca da pessoa: parâmetro da empresa, sem folha e sem
 * salário (decisão do dono, 06/10/2026). Mudar o valor muda o custo de mão de
 * obra de todo período consultado que cair na vigência — por isso o motivo.
 */
import { formatarDia, formatarReais, interpretarNumero } from '@/lib/formato';
import { faltaNoMotivo } from '@/lib/motivo';

import type { FuncaoComValorHora, NovoValorHora, VigenciaDoValorHora } from './api';

export interface ValoresDoValorHora {
  readonly funcaoId: string;
  readonly desde: string;
  readonly valor: string;
  readonly motivo: string;
}

/**
 * A data de início sugerida: o primeiro dia do MÊS ANTERIOR. Começar hoje
 * deixaria o mês fechado (o da margem da ficha e do resultado) sem valor, e a
 * comparação com o período anterior subiria só porque a base estava sem mão de
 * obra — a variação inflada que a R02 proíbe.
 */
export function inicioSugerido(hoje: Date): string {
  const ano = hoje.getMonth() === 0 ? hoje.getFullYear() - 1 : hoje.getFullYear();
  const mes = hoje.getMonth() === 0 ? 12 : hoje.getMonth();
  return `${String(ano)}-${String(mes).padStart(2, '0')}-01`;
}

/** O que falta, por campo. Nada falta: objeto vazio. */
export function faltaNoValorHora(v: ValoresDoValorHora): Partial<Record<keyof ValoresDoValorHora, string>> {
  const falta: Partial<Record<keyof ValoresDoValorHora, string>> = {};
  if (v.funcaoId === '') falta.funcaoId = 'Escolha a função.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v.desde)) falta.desde = 'Informe a data em que o valor passa a valer.';
  const valor = interpretarNumero(v.valor);
  if (valor === null || valor <= 0) falta.valor = 'Informe o valor da hora em reais, maior que zero, como 48,50.';
  const motivo = faltaNoMotivo(v.motivo);
  if (motivo) falta.motivo = motivo;
  return falta;
}

export function corpoDoValorHora(v: ValoresDoValorHora): NovoValorHora {
  return { funcaoId: v.funcaoId, desde: v.desde, valorHora: interpretarNumero(v.valor) ?? 0, motivo: v.motivo.trim() };
}

/** "R$ 48,50/h", ou nulo sem valor vigente. */
export function valorHoraEscrito(valor: number | null): string | null {
  return valor === null ? null : `${formatarReais(valor)}/h`;
}

/** "desde 01/09/2026" ou "de 01/09/2026 a 30/09/2026" — o fim é exclusivo no banco, e a tela mostra o último dia. */
export function vigenciaEscrita(v: Pick<VigenciaDoValorHora, 'desde' | 'ate'>): string {
  if (v.ate === null) return `desde ${formatarDia(v.desde)}`;
  const ultimo = new Date(`${v.ate}T12:00:00`);
  ultimo.setDate(ultimo.getDate() - 1);
  const ultimoDia = `${String(ultimo.getFullYear())}-${String(ultimo.getMonth() + 1).padStart(2, '0')}-${String(ultimo.getDate()).padStart(2, '0')}`;
  return `de ${formatarDia(v.desde)} a ${formatarDia(ultimoDia)}`;
}

/**
 * O efeito no retorno (R04): antes → depois do valor na data escolhida. A
 * vigência que contém a data é a que muda; sem ela, "sem valor".
 */
export function efeitoDoValorHora(funcao: FuncaoComValorHora | undefined, desde: string, novo: number) {
  const anterior = funcao?.historico.find((h) => h.desde <= desde && (h.ate === null || h.ate > desde)) ?? null;
  return {
    rotulo: `Valor-hora desde ${formatarDia(desde)}`,
    antes: anterior ? `${formatarReais(anterior.valorHora)}/h` : 'sem valor',
    depois: `${formatarReais(novo)}/h`,
  };
}

/** Quantas funções ativas estão sem valor hoje — a lacuna que a lista mostra. */
export function funcoesSemValor(funcoes: readonly FuncaoComValorHora[]): number {
  return funcoes.filter((f) => f.ativo && f.valorHoraVigente === null).length;
}
